import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LIMITI_POSTA, type CartaMail as DatiMail } from '@masamune/shared';
import { Icon } from '../../components/ui/Icon';
import { rinfrescaDopoModifiche, useScaduta } from './carteApi';
import { aggiungiFile, bozzaMail, fraseCartaMail, lasciaMail, mandaMail, PAROLE_POSTA, pesoFile, useCarteMail, type CorrezioniMail } from './postaApi';
import './carte.css';
import './cartaMail.css';

/** Oltre queste righe il testo si chiude, e «Mostra tutto» lo apre. */
const RIGHE_APERTE = 12;

/**
 * La carta «Mail da mandare» nella risposta di MUNE (Fase 2H, 29/10/2026; os-planning/ai/
 * fase-2h-mail.md, «Interfaccia»). Arriva con l'evento `mail` dopo che MUNE ha preparato la mail
 * (prepara_mail): mostra da chi parte, a chi va, l'oggetto e il testo intero, e parte solo col clic
 * su «Manda». «Salva in Gmail» la lascia tra le bozze, «Lascia stare» la chiude. Lo stile della
 * cornice è quello della carta delle modifiche (carte.css, cc-); il resto sta in cartaMail.css.
 * Al telefono gli stessi pezzi, coi tasti uno sotto l'altro a tutta larghezza.
 *
 * Finché è da mandare il testo è un campo (Lorenzo, 30/10/2026: «si possono modificare le email
 * direttamente selezionando il testo»): parte il testo corretto, e resta quello nella carta. Lo
 * stesso giorno anche l'oggetto e gli allegati («inserire anche allegati nelle email e modificare
 * anche l'oggetto»): i file restano nel browser finché non si preme Manda o Salva in Gmail, e
 * partono con quel clic; dopo la carta ne mostra i nomi.
 */
export function CartaMail({ carta, mobile }: { carta: DatiMail; mobile: boolean }): JSX.Element {
  const idTitolo = useId();
  const qc = useQueryClient();
  const decisa = useCarteMail((s) => s.carte[carta.id]);
  const metti = useCarteMail((s) => s.metti);
  const [lavoro, setLavoro] = useState<null | 'manda' | 'bozza' | 'lascia'>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const [aperta, setAperta] = useState(false);
  const adesso = decisa ?? carta;
  const scaduta = useScaduta(adesso.stato === 'PROPOSTA' ? adesso.scade : null);
  const c: DatiMail = adesso.stato === 'PROPOSTA' && scaduta ? { ...adesso, stato: 'SCADUTA' } : adesso;
  const aperto = c.stato === 'PROPOSTA';
  // Testo, oggetto e file da correggere, finché la mail è da mandare.
  const [scritto, setScritto] = useState(carta.testo);
  const [oggetto, setOggetto] = useState(carta.oggetto);
  const [file, setFile] = useState<File[]>([]);
  const scelta = useRef<HTMLInputElement>(null);
  const campo = useRef<HTMLTextAreaElement>(null);
  // Il campo cresce col testo: niente barra di scorrimento dentro la carta.
  useLayoutEffect(() => {
    const el = campo.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [scritto, aperto, mobile]);
  const vuoto = !scritto.trim();
  const senzaOggetto = !oggetto.trim();

  const scegli = (scelti: File[]) => {
    const { file: nuovi, fuori } = aggiungiFile(file, scelti);
    setFile(nuovi);
    setProblema(fuori);
  };

  const fai = async (quale: 'manda' | 'bozza' | 'lascia') => {
    setLavoro(quale);
    setProblema(null);
    try {
      // Solo quello che è cambiato: il resto parte com'è nella carta di MUNE.
      const correzioni: CorrezioniMail = {
        ...(scritto.trim() !== c.testo.trim() ? { testo: scritto } : {}),
        ...(oggetto.trim() !== c.oggetto.trim() ? { oggetto } : {}),
        ...(file.length ? { file } : {}),
      };
      const fatta = await (quale === 'manda' ? mandaMail(c.id, correzioni) : quale === 'bozza' ? bozzaMail(c.id, correzioni) : lasciaMail(c.id));
      metti(fatta);
      // Mandata, la mail è nelle comunicazioni del cliente e nel registro: le pagine aperte si rileggono.
      if (fatta.stato === 'MANDATA') rinfrescaDopoModifiche(qc);
    } catch (e) {
      const codice = (e as { code?: string }).code;
      if (codice === 'MAIL_SCADUTA') metti({ ...c, stato: 'SCADUTA' });
      setProblema((e as Error)?.message || 'Non è riuscito: riprova');
    } finally {
      setLavoro(null);
    }
  };

  const righe = c.testo.split('\n');
  const lungo = righe.length > RIGHE_APERTE || c.testo.length > 900;
  const testo = lungo && !aperta ? `${righe.slice(0, RIGHE_APERTE).join('\n').slice(0, 900).trimEnd()}…` : c.testo;
  const occupata = lavoro !== null;

  return (
    <div className={`cc cmail${mobile ? ' cc--m cmail--m' : ''}`} role="group" aria-labelledby={idTitolo} data-stato={c.stato}>
      <div className="cc-hd">
        <h3 className="cc-titolo" id={idTitolo}>{PAROLE_POSTA.titolo[c.stato]}</h3>
        <p className="cc-stato" role="status">
          {fraseCartaMail(c)}
          {c.link && c.stato !== 'PROPOSTA' && (
            <>
              {' · '}
              <a className="cmail-link" href={c.link} target="_blank" rel="noreferrer">{PAROLE_POSTA.apri}</a>
            </>
          )}
        </p>
      </div>
      <dl className="cmail-campi">
        <Campo nome={PAROLE_POSTA.campi.da} valore={c.da} />
        <Campo nome={PAROLE_POSTA.campi.a} valore={c.a.join(', ')} />
        {c.cc.length > 0 && <Campo nome={PAROLE_POSTA.campi.cc} valore={c.cc.join(', ')} />}
        {aperto ? (
          <div className="cmail-campo">
            <dt>{PAROLE_POSTA.campi.oggetto}</dt>
            <dd>
              <input
                className="cmail-oggetto"
                value={oggetto}
                onChange={(e) => setOggetto(e.target.value)}
                aria-label={PAROLE_POSTA.etichettaOggetto}
                maxLength={LIMITI_POSTA.caratteriOggetto}
                spellCheck
                lang="it"
                disabled={occupata}
              />
            </dd>
          </div>
        ) : (
          <Campo nome={PAROLE_POSTA.campi.oggetto} valore={c.oggetto} />
        )}
        {!aperto && c.allegati.length > 0 && (
          <Campo nome={PAROLE_POSTA.campi.allegati} valore={c.allegati.map((a) => a.nome).join(', ')} />
        )}
      </dl>
      {aperto ? (
        <div className="cmail-testo cmail-testo--campo">
          <textarea
            ref={campo}
            className="cmail-scrivi"
            value={scritto}
            onChange={(e) => setScritto(e.target.value)}
            aria-label={PAROLE_POSTA.etichettaTesto}
            spellCheck
            lang="it"
            rows={4}
            disabled={occupata}
          />
        </div>
      ) : (
        <div className="cmail-testo" data-spenta={c.stato !== 'MANDATA' ? 'true' : undefined}>
          {testo}
          {lungo && (
            <button type="button" className="cmail-tutto" onClick={() => setAperta((x) => !x)} aria-expanded={aperta}>
              {aperta ? PAROLE_POSTA.meno : PAROLE_POSTA.tutto}
            </button>
          )}
        </div>
      )}
      {aperto && (
        <div className="cmail-allegati">
          {file.length > 0 && (
            <ul className="cmail-file">
              {file.map((f, i) => (
                <li key={`${f.name}-${f.size}`} className="cmail-pezzo" title={f.name}>
                  <Icon name="file" size={13} className="cmail-pezzo-icona" />
                  <span className="cmail-pezzo-nome">{f.name}</span>
                  <span className="cmail-pezzo-peso">{pesoFile(f.size)}</span>
                  <button
                    type="button"
                    className="cmail-pezzo-x m-hit"
                    aria-label={PAROLE_POSTA.togli(f.name)}
                    title={PAROLE_POSTA.togli(f.name)}
                    disabled={occupata}
                    onClick={() => { setFile((x) => x.filter((_, j) => j !== i)); setProblema(null); }}
                  >
                    <Icon name="x" size={12} strokeWidth={2} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="btn btn-sm btn-ghost cmail-allega" disabled={occupata} onClick={() => scelta.current?.click()}>
            <Icon name="paperclip" size={12} /> {PAROLE_POSTA.allega}
          </button>
          <input
            ref={scelta}
            type="file"
            className="cmail-scelta"
            tabIndex={-1}
            aria-hidden="true"
            multiple
            onChange={(e) => {
              const scelti = Array.from(e.currentTarget.files ?? []);
              e.currentTarget.value = '';
              if (scelti.length) scegli(scelti);
            }}
          />
        </div>
      )}
      {aperto && (
        <div className="cc-ft cmail-ft">
          <p className="cmail-nota">{vuoto ? PAROLE_POSTA.testoVuoto : senzaOggetto ? PAROLE_POSTA.oggettoVuoto : `${PAROLE_POSTA.correggi} ${PAROLE_POSTA.nonSiRitira}`}</p>
          <div className="cmail-tasti">
            <button type="button" className="btn btn-sm btn-primary" disabled={occupata || vuoto || senzaOggetto} onClick={() => void fai('manda')}>
              <Icon name="send" size={12} /> {lavoro === 'manda' ? PAROLE_POSTA.mando : PAROLE_POSTA.manda}
            </button>
            <button type="button" className="btn btn-sm" disabled={occupata || vuoto || senzaOggetto} onClick={() => void fai('bozza')}>
              {lavoro === 'bozza' ? PAROLE_POSTA.salvo : PAROLE_POSTA.bozza}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={occupata} onClick={() => void fai('lascia')}>
              {PAROLE_POSTA.lascia}
            </button>
          </div>
          {problema && <p className="cc-problema" role="alert">{problema}</p>}
        </div>
      )}
      {!aperto && problema && <div className="cc-ft"><p className="cc-problema" role="alert">{problema}</p></div>}
    </div>
  );
}

function Campo({ nome, valore }: { nome: string; valore: string }) {
  return (
    <div className="cmail-campo">
      <dt>{nome}</dt>
      <dd>{valore}</dd>
    </div>
  );
}
